// Desktop Create Property Side Panel - using shared hook
import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { PhotoUploadSection } from '@/components/common/admin/mobile/PhotoUploadSection';
import { DEFAULT_MAINTENANCE_SCHEDULE } from '@/constants/admin/propertyFormDefaults';
import { useCreateProperty } from '@/hooks/admin/useCreateProperty';
import { usePropertyFormStyles } from './propertyFormStyles';
import {
  PropertyGeneralInfoSection,
  PropertyPMSTypeSection,
  PropertyYardiSection,
  PropertyCardConnectSection,
  PropertyDetailsSection,
  PropertyFeesSection,
  PropertyMaintenanceSection,
  DayOfWeek,
  MaintenanceDay,
} from '../form-sections';

interface DesktopCreatePropertyPanelProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DesktopCreatePropertyPanel: React.FC<DesktopCreatePropertyPanelProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const styles = usePropertyFormStyles();
  const {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
  } = useCreateProperty({ visible, onClose, onSuccess });

  // Desktop-specific state (not in mobile)
  const [photos, setPhotos] = useState<any[]>([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(DEFAULT_MAINTENANCE_SCHEDULE);

  // Reset desktop-specific state when panel closes
  useEffect(() => {
    if (!visible) {
      setPhotos([]);
      setMaintenanceSchedule(DEFAULT_MAINTENANCE_SCHEDULE);
    }
  }, [visible]);

  // Handle maintenance day toggle
  const handleDayToggle = (day: DayOfWeek) => {
    setMaintenanceSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      },
    }));
  };

  // Handle time change
  const handleTimeChange = (day: DayOfWeek, field: 'start' | 'end', value: string) => {
    setMaintenanceSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field === 'start' ? 'startTime' : 'endTime']: value,
      },
    }));
  };

  // Handle appending new photos
  const handleAppendPhotos = (newPhotos: any[]) => {
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  return (
    <DesktopSidePanel
      visible={visible}
      onClose={onClose}
      width={520}
    >
      <DesktopPanelHeader
        title="Create Property"
        onClose={onClose}
      />

      <DesktopPanelBody>
        <ScrollView style={styles.container} contentContainerStyle={{ paddingHorizontal: 18, paddingTop: 8 }} showsVerticalScrollIndicator={false}>
          {/* General Information */}
          <PropertyGeneralInfoSection
            marketingName={formData.marketingName}
            externalId={formData.externalId}
            organizationId={formData.organizationId}
            nameError={errors.marketingName}
            externalIdError={errors.externalId}
            organizationIdError={errors.organizationId}
            onMarketingNameChange={(value) => handleInputChange('marketingName', value)}
            onExternalIdChange={(value) => handleInputChange('externalId', value)}
            onOrganizationIdChange={(value) => handleInputChange('organizationId', value)}
            disabled={isLoading}
          />

          {/* PMS Type Selection */}
          <PropertyPMSTypeSection
            pmsType={formData.pmsType}
            onPmsTypeChange={(value) => handleInputChange('pmsType', value)}
            disabled={isLoading}
          />

          {/* Yardi Configuration - Show only if Yardi is selected */}
          {formData.pmsType === 'yardi' && (
            <PropertyYardiSection
              yardiDomain={formData.yardiDomain}
              yardiUsername={formData.yardiUsername}
              yardiPassword={formData.yardiPassword}
              yardiServerName={formData.yardiServerName}
              yardiDatabase={formData.yardiDatabase}
              yardiBpInterfaceEntity={formData.yardiBpInterfaceEntity}
              yardiBpInterfaceLicense={formData.yardiBpInterfaceLicense}
              onYardiDomainChange={(value) => handleInputChange('yardiDomain', value)}
              onYardiUsernameChange={(value) => handleInputChange('yardiUsername', value)}
              onYardiPasswordChange={(value) => handleInputChange('yardiPassword', value)}
              onYardiServerNameChange={(value) => handleInputChange('yardiServerName', value)}
              onYardiDatabaseChange={(value) => handleInputChange('yardiDatabase', value)}
              onYardiBpInterfaceEntityChange={(value) => handleInputChange('yardiBpInterfaceEntity', value)}
              onYardiBpInterfaceLicenseChange={(value) => handleInputChange('yardiBpInterfaceLicense', value)}
              disabled={isLoading}
            />
          )}

          {/* Card Connect Settings */}
          <PropertyCardConnectSection
            creditCardMerchantAccountId={formData.creditCardMerchantAccountId}
            achMerchantAccountId={formData.achMerchantAccountId}
            onCreditCardChange={(value) => handleInputChange('creditCardMerchantAccountId', value)}
            onAchChange={(value) => handleInputChange('achMerchantAccountId', value)}
            disabled={isLoading}
          />

          {/* Property Images */}
          <View style={styles.section}>
            <PhotoUploadSection
              title="Property Images"
              subtitle="Add photos of your property (optional)"
              photos={photos}
              onPhotosChange={setPhotos}
              onAppendPhotos={handleAppendPhotos}
              maxPhotos={10}
              photoType="event"
            />
          </View>

          {/* Property Details */}
          <PropertyDetailsSection
            propertyEmailAddress={formData.propertyEmailAddress}
            propertyPhoneNumber={formData.propertyPhoneNumber}
            propertyUrl={formData.propertyUrl}
            propertyFaqUrl={formData.propertyFaqUrl}
            propertyResidentHandbook={formData.propertyResidentHandbook}
            propertyCbxFilePath={formData.propertyCbxFilePath}
            emailError={errors.propertyEmailAddress}
            phoneError={errors.propertyPhoneNumber}
            urlError={errors.propertyUrl}
            onEmailChange={(value) => handleInputChange('propertyEmailAddress', value)}
            onPhoneChange={(value) => handleInputChange('propertyPhoneNumber', value)}
            onUrlChange={(value) => handleInputChange('propertyUrl', value)}
            onFaqUrlChange={(value) => handleInputChange('propertyFaqUrl', value)}
            onHandbookChange={(value) => handleInputChange('propertyResidentHandbook', value)}
            onCbxFilePathChange={(value) => handleInputChange('propertyCbxFilePath', value)}
            disabled={isLoading}
          />

          {/* Property Fees */}
          <PropertyFeesSection
            propertyNsfFee={formData.propertyNsfFee}
            propertyLateDayStart={formData.propertyLateDayStart}
            propertyLateDayFee={formData.propertyLateDayFee}
            propertyLateDailyFee={formData.propertyLateDailyFee}
            propertyMaxMonthlyFee={formData.propertyMaxMonthlyFee}
            nsfFeeError={errors.propertyNsfFee}
            lateDayFeeError={errors.propertyLateDayFee}
            lateDailyFeeError={errors.propertyLateDailyFee}
            maxMonthlyFeeError={errors.propertyMaxMonthlyFee}
            onNsfFeeChange={(value) => handleInputChange('propertyNsfFee', value)}
            onLateDayStartChange={(value) => handleInputChange('propertyLateDayStart', value)}
            onLateDayFeeChange={(value) => handleInputChange('propertyLateDayFee', value)}
            onLateDailyFeeChange={(value) => handleInputChange('propertyLateDailyFee', value)}
            onMaxMonthlyFeeChange={(value) => handleInputChange('propertyMaxMonthlyFee', value)}
            disabled={isLoading}
          />

          {/* Maintenance Schedule */}
          <PropertyMaintenanceSection
            maintenanceSchedule={maintenanceSchedule}
            onDayToggle={handleDayToggle}
            onTimeChange={handleTimeChange}
            disabled={isLoading}
          />

          {/* Actions */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              style={[
                styles.button,
                styles.createButton,
                isLoading && styles.disabledButton,
              ]}
              onPress={handleSubmit}
              disabled={isLoading}
              activeOpacity={0.7}
            >
              {isLoading ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.createButtonText}>Create Property</Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </DesktopPanelBody>
    </DesktopSidePanel>
  );
};

